<template>
  <div class="accessories-grid">
    <table-filter-chips
      v-if="activeFilterChips.length"
      :chips="activeFilterChips"
      @remove="removeFilter"
      @clear-all="clearFilters"
    />

    <div class="ds-table">
      <div class="ds-table__scroll accessories-grid__scroll">
        <table :style="{ minWidth: tableMinWidth }">
          <sortable-table-header
            :columns="tableColumns"
            :filters="filters"
            :sort-column="sortColumn"
            :sort-direction="sortDirection"
            @toggle-sort="toggleSort"
            @set-filter="setFilter"
            @clear-filters="clearMultipleFilters"
          />
          <tbody>
            <tr v-for="(item, idx) in pagedItems" :key="itemKey(item, idx)">
              <td class="ds-table__code">{{ item.ID }}</td>
              <td class="accessories-grid__name">{{ item.Name || '-' }}</td>
              <td>{{ item.AnalysisCode || '-' }}</td>
              <td>
                <span v-if="item.Category" class="ds-chip ds-chip--brand">{{ item.Category }}</span>
                <span v-else>-</span>
              </td>
              <td>
                <span
                  class="ds-chip"
                  :class="item.Standard ? 'ds-chip--positive' : 'ds-chip--neutral'"
                >
                  {{ item.Standard ? 'Ja' : 'Nee' }}
                </span>
              </td>
              <td class="accessories-grid__price">
                {{ formatPrice(item.Price && item.Price.Excl) }}
              </td>
              <td class="accessories-grid__price accessories-grid__price--muted">
                {{ formatPrice(item.Price && item.Price.Incl) }}
              </td>
            </tr>
            <tr v-if="!pagedItems.length">
              <td colspan="7" class="ds-table__empty">
                Geen resultaten gevonden voor de huidige filters.
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <ds-pagination
        v-model:page="currentPage"
        v-model:page-size="pageSize"
        :total="filteredItems.length"
        :page-size-options="[100, 250, 500]"
      />
    </div>
  </div>
</template>

<script>
import SortableTableHeader from '../../warningbanner/SortableTableHeader.vue'
import TableFilterChips from '../../warningbanner/TableFilterChips.vue'
import DsPagination from '@/components/ds/molecules/dsPagination/DsPagination.vue'
import CurrencyHelper from '@/helpers/currencyHelper'

const EMPTY_FILTERS = { ID: '', Name: '', AnalysisCode: '', Category: '', Standard: '' }

const matchesText = (value, filter) =>
  !filter ||
  String(value ?? '')
    .toLowerCase()
    .includes(filter.toLowerCase())

export default {
  name: 'DealerAccessoriesGrid',
  components: { SortableTableHeader, TableFilterChips, DsPagination },
  props: {
    items: { type: Array, required: true },
  },
  data() {
    return {
      filters: { ...EMPTY_FILTERS },
      sortColumn: 'ID',
      sortDirection: 'asc',
      currentPage: 1,
      pageSize: 100,
    }
  },
  computed: {
    categoryOptions() {
      const categories = [...new Set(this.items.map((i) => i.Category).filter(Boolean))]
      return categories.map((value) => ({ value, label: value }))
    },
    tableColumns() {
      return [
        {
          key: 'ID',
          label: 'Accessoire ID',
          width: '150px',
          filter: 'text',
          placeholder: 'Zoek op ID...',
        },
        {
          key: 'Name',
          label: 'Omschrijving',
          width: '300px',
          filter: 'text',
          placeholder: 'Zoek op omschrijving...',
        },
        {
          key: 'AnalysisCode',
          label: 'Analyse code',
          width: '140px',
          filter: 'text',
          placeholder: 'Zoek op analyse code...',
        },
        {
          key: 'Category',
          label: 'Category',
          width: '190px',
          filter: 'select',
          options: this.categoryOptions,
        },
        {
          key: 'Standard',
          label: 'Standaard',
          width: '130px',
          filter: 'select',
          options: [
            { value: 'ja', label: 'Ja' },
            { value: 'nee', label: 'Nee' },
          ],
        },
        { key: 'Price.Excl', label: 'Prijs excl. BTW', width: '150px', sortable: true },
        { key: 'Price.Incl', label: 'Prijs incl. BTW', width: '150px', sortable: true },
      ]
    },
    filteredItems() {
      return this.items.filter((item) => {
        const standardFilter = this.filters.Standard
        const standardMatches =
          !standardFilter || (standardFilter === 'ja' ? !!item.Standard : !item.Standard)
        return (
          matchesText(item.ID, this.filters.ID) &&
          matchesText(item.Name, this.filters.Name) &&
          matchesText(item.AnalysisCode, this.filters.AnalysisCode) &&
          (!this.filters.Category || item.Category === this.filters.Category) &&
          standardMatches
        )
      })
    },
    activeFilterChips() {
      const chips = []
      if (this.filters.ID) chips.push({ key: 'ID', label: `Accessoire ID: "${this.filters.ID}"` })
      if (this.filters.Name)
        chips.push({ key: 'Name', label: `Omschrijving: "${this.filters.Name}"` })
      if (this.filters.AnalysisCode)
        chips.push({ key: 'AnalysisCode', label: `Analyse code: "${this.filters.AnalysisCode}"` })
      if (this.filters.Category)
        chips.push({ key: 'Category', label: `Category: ${this.filters.Category}` })
      if (this.filters.Standard)
        chips.push({
          key: 'Standard',
          label: `Standaard: ${this.filters.Standard === 'ja' ? 'Ja' : 'Nee'}`,
        })
      return chips
    },
    sortedItems() {
      if (!this.sortColumn) return this.filteredItems
      const col = this.sortColumn
      const dir = this.sortDirection === 'asc' ? 1 : -1
      return [...this.filteredItems].sort((a, b) => this.compareByColumn(a, b, col) * dir)
    },
    pagedItems() {
      const start = (this.currentPage - 1) * this.pageSize
      return this.sortedItems.slice(start, start + this.pageSize)
    },
    // Sum of the declared column widths. Without a definite table width,
    // `table-layout: fixed` lets cell content inflate the columns instead of
    // the other way round.
    tableMinWidth() {
      const columns = this.tableColumns.reduce(
        (sum, col) => sum + (parseFloat(col.width) || 140),
        0
      )
      return `${columns}px`
    },
  },
  watch: {
    filters: {
      deep: true,
      handler() {
        this.currentPage = 1
      },
    },
    items() {
      this.currentPage = 1
    },
  },
  methods: {
    formatPrice(value) {
      return value === undefined || value === null ? '-' : CurrencyHelper.currency(value)
    },
    itemKey(item, idx) {
      return `${item.ID}-${idx}`
    },
    compareByColumn(a, b, col) {
      if (col === 'Price.Excl' || col === 'Price.Incl') {
        const key = col === 'Price.Excl' ? 'Excl' : 'Incl'
        return (a.Price?.[key] ?? 0) - (b.Price?.[key] ?? 0)
      }
      return String(a[col] ?? '').localeCompare(String(b[col] ?? ''), 'nl')
    },
    setFilter(key, value) {
      this.filters[key] = value
    },
    clearMultipleFilters(keys) {
      keys.forEach((key) => {
        this.filters[key] = ''
      })
    },
    removeFilter(key) {
      this.filters[key] = ''
    },
    clearFilters() {
      this.filters = { ...EMPTY_FILTERS }
    },
    toggleSort(column) {
      if (this.sortColumn === column) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc'
        if (this.sortDirection === 'asc') this.sortColumn = null
      } else {
        this.sortColumn = column
        this.sortDirection = 'asc'
      }
    },
  },
}
</script>

<style lang="scss" scoped>
// Layout only. The table, chips, code cells and pagination come from the
// design system (styles/ds/). See styles/ds/README.md before adding anything
// here.

.accessories-grid {
  display: flex;
  flex-direction: column;
  gap: var(--ds-space-3);
}

// Keeps the sticky header useful with 500-row pages: the grid scrolls inside
// the card rather than stretching the whole page.
.accessories-grid__scroll {
  max-height: calc(100vh - 420px);
  min-height: 200px;
}

.accessories-grid__name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 500;
}

.accessories-grid__price {
  text-align: right;
  font-variant-numeric: tabular-nums;
  font-weight: 600;

  &--muted {
    font-weight: 400;
    color: var(--ds-color-text-muted);
  }
}
</style>
